import { Component, Input } from '@angular/core';
import { AlertService } from '@services/helpers/alert/alert.service';
import { Alert, AlertType } from '@models/app/alert/Alert';
import { animate, style, transition, trigger } from '@angular/animations';
import { IconComponent } from '../icon/icon.component';
import { AsyncPipe, NgStyle } from '@angular/common';

@Component({
    selector: 'app-alert',
    templateUrl: './alert.component.html',
    styleUrls: ['./alert.component.scss'],
    animations: [
        trigger('fade', [
            transition('void => *', [
                style({ opacity: 0 }),
                animate(200, style({ opacity: 1 })),
            ]),
            transition('* => void', [animate(200, style({ opacity: 0 }))]),
        ]),
    ],
    standalone: true,
    imports: [IconComponent, AsyncPipe, NgStyle]
})
export class AlertComponent {
  @Input() fade = true;
  AlertType = AlertType;

  constructor(public alertService: AlertService) {}

  removeAlert(alert: Alert): void {
    this.alertService.removeAlert(alert)
  }

  cssClass(alert: Alert): string {
    if (!alert) {
      return null;
    }

    const classes = ['alert-shell'];

    const alertTypeClass = {
      [AlertType.Success]: 'alert-success',
      [AlertType.Error]: 'alert-danger',
      [AlertType.Info]: 'alert-info',
      [AlertType.Warning]: 'alert-warning',
      [AlertType.Live]: 'alert-live',
    };

    classes.push(alertTypeClass[alert.type]);

    if (alert.fade) {
      classes.push('fade');
    }

    return classes.join(' ');
  }

  alertStyle(alert: Alert): Record<string, string> {
    const palette = {
      [AlertType.Success]: {
        accent: 'rgb(16 185 129)',
        accentGlow: 'rgba(16, 185, 129, 0.45)',
        accentForeground: 'rgb(209 250 229)',
      },
      [AlertType.Error]: {
        accent: 'rgb(248 113 113)',
        accentGlow: 'rgba(248, 113, 113, 0.45)',
        accentForeground: 'rgb(255 228 230)',
      },
      [AlertType.Info]: {
        accent: 'rgb(59 130 246)',
        accentGlow: 'rgba(59, 130, 246, 0.45)',
        accentForeground: 'rgb(219 234 254)',
      },
      [AlertType.Warning]: {
        accent: 'rgb(245 158 11)',
        accentGlow: 'rgba(245, 158, 11, 0.45)',
        accentForeground: 'rgb(254 243 199)',
      },
      [AlertType.Live]: {
        accent: 'rgb(34 211 238)',
        accentGlow: 'rgba(34, 211, 238, 0.45)',
        accentForeground: 'rgb(207 250 254)',
      },
    }[alert?.type];

    return {
      '--alert-accent': palette.accent,
      '--alert-accent-glow': palette.accentGlow,
      '--alert-accent-foreground': palette.accentForeground,
      'background-image':
        'linear-gradient(180deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.015) 100%), linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(2, 6, 23, 0.98))',
    };
  }

  alertTypeClass(alert: Alert): string {
    switch (alert?.type) {
      case AlertType.Success:
        return 'border-emerald-400/35';
      case AlertType.Error:
        return 'border-red-400/35';
      case AlertType.Info:
        return 'border-blue-400/35';
      case AlertType.Warning:
        return 'border-amber-400/35';
      case AlertType.Live:
        return 'border-cyan-400/35';
      default:
        return 'border-white/10';
    }
  }

  alertLabel(alert: Alert): string {
    switch (alert?.type) {
      case AlertType.Success:
        return 'Success';
      case AlertType.Error:
        return 'Error';
      case AlertType.Info:
        return 'Info';
      case AlertType.Warning:
        return 'Warning';
      case AlertType.Live:
        return 'Live';
      default:
        return 'Alert';
    }
  }

  alertIconClass(alert: Alert): string {
    switch (alert?.type) {
      case AlertType.Success:
        return 'text-emerald-100';
      case AlertType.Error:
        return 'text-red-100';
      case AlertType.Info:
        return 'text-blue-100';
      case AlertType.Warning:
        return 'text-amber-100';
      case AlertType.Live:
        return 'text-cyan-100';
      default:
        return 'text-slate-100';
    }
  }
}
